import { Autocomplete, Checkbox, Chip, Tooltip } from "@mui/material";
import { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { MemberListItem } from "components/Tooltips/ClaimRewardModal";
import { OLTheme, defaultOLTheme } from "contexts/Theme/ThemeProvider";
import React, { ChangeEvent } from "react";
import { BackendMemberModel } from "types/members";
import { LabelValuePair } from "utilities/objects";
import { MultiTextInput } from "./MultiTextInput";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    input: {
      flex: 1,
      marginTop: theme.spacing(10),
      width: "100%",
      [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(0),
        width: "auto",
      },
    },
    close: {
      minWidth: "16px !important",
      height: "16px !important",
    },
    nestedInput: {
      width: "100%",
      "[dir='rtl'] & .MuiChip-deleteIcon": {
        margin: "0 -6px 0 5px",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        minHeight: "48px",
        padding: theme.spacing(4, 7),
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "8px",
      },
      "& .MuiInputBase-input": {
        padding: "0",
      },
    },
    selectedItem: { background: "none" },
    itemContainer: {
      display: "flex",
      alignItems: "start",
      "&:hover": { backgroundColor: "#F5F5F5" },
    },
    itemLabel: { marginTop: theme.spacing(4) },
  }),
);

export const CustomAutocomplete = withStyles(() => ({
  inputRoot: {
    paddingRight: "65px !important",
  },
  tag: {
    borderRadius: 4,
    backgroundColor: `${defaultOLTheme.gray100} !important`,
    "& svg": {
      height: 19,
    },
  },
  endAdornment: {
    borderRadius: "0 8px 8px 0 !important",
    display: "flex",
    right: "0 !important",
    width: 56,
    height: "100%",
    "& button": {
      borderRadius: "0 8px 8px 0 !important",
      width: "100%",
    },
  },
  input: {
    "&::placeholder": {
      textOverflow: "ellipsis !important",
    },
  },
  popupIndicator: {
    "& svg": {
      width: "24px",
      height: "24px",
    },
  },
}))(Autocomplete);

// Component was restored for task OLOY-8181 (previously as MultiWithSelect)
// After implementing a new component dedicated to the Custom member list,
// this component should be removed.
export function CustomMemberListSelect({
  className,
  currentValue,
  "data-test-id": dataTestId,
  defaultValue,
  disabled,
  formField,
  helperText,
  items,
  label,
  onBlur,
  onChange,
  onTyping,
  placeholder,
  onInputChange,
  loading,
  inputValue,
  listOfMembers,
}: {
  className?: string;
  currentValue?: LabelValuePair[];
  "data-test-id"?: string;
  defaultValue?: LabelValuePair[];
  disabled?: boolean;
  formField: any;
  helperText?: string;
  items: LabelValuePair[];
  label: React.ReactNode;
  onBlur?: () => void;
  onChange: (newValue: LabelValuePair[]) => void;
  onTyping?: (newValue: string) => void;
  placeholder?: string;
  onInputChange?: (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  loading?: boolean;
  inputValue?: string;
  listOfMembers?: BackendMemberModel[];
}) {
  const classes = useStyles();

  return (
    <CustomAutocomplete
      className={`${classes.input} ${className}`}
      disableClearable
      disabled={disabled}
      inputValue={inputValue}
      {...formField}
      name={formField.name}
      popupIcon={<ChevronDownIcon />}
      getOptionLabel={(option: LabelValuePair) =>
        `${option.label || option.value}`
      }
      renderOption={(props, option: LabelValuePair, { selected }) => {
        const member = listOfMembers?.find(
          (member) => member?.customerId === option?.value,
        );
        return (
          <li {...props} className={`${selected ? classes.selectedItem : ""}`}>
            <div className={classes.itemContainer}>
              <div>
                <Checkbox checked={selected} />
              </div>
              <div className={classes.itemLabel}>
                <MemberListItem
                  props={{ ...props, "data-hj-suppress": true }}
                  member={member}
                />
              </div>
            </div>
          </li>
        );
      }}
      isOptionEqualToValue={(
        option: { value: string },
        value: { value: string },
      ) => option.value === value.value}
      multiple
      onChange={(_, newValue: LabelValuePair[]) => {
        onChange(newValue);
      }}
      onBlur={() => onBlur?.()}
      onInputChange={(
        event: React.ChangeEvent<{}>,
        value: string,
        reason: AutocompleteInputChangeReason,
      ) => onInputChange?.(event, value, reason)}
      options={items}
      defaultValue={defaultValue}
      renderInput={(params) => (
        <MultiTextInput
          {...params}
          className={classes.nestedInput}
          helperText={helperText}
          label={label}
          name={formField.name}
          onChange={(
            event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            onTyping?.(event.target.value);
          }}
          placeholder={placeholder}
          data-test-id={dataTestId || "multiwithselect-container"}
        />
      )}
      renderTags={(value: LabelValuePair[], getTagProps) =>
        value?.map?.((option: LabelValuePair, index: number) => (
          <Tooltip title={<span data-hj-suppress>{option?.label || ""}</span>}>
            <Chip
              deleteIcon={
                <XCloseIcon
                  data-test-id={`chips-${index}-remove-button`}
                  className={classes.close}
                />
              }
              label={option?.label || option?.value}
              {...getTagProps({ index })}
              data-test-id={`chips-${index}`}
              data-hj-suppress
            />
          </Tooltip>
        ))
      }
      value={currentValue}
      loading={loading}
    />
  );
}
